/* eslint  no-undef: "off"*/
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { ofMerge } from "./otelHelpers";
import { BatchSpanProcessor, SamplingDecision } from "@opentelemetry/sdk-trace-base";
import { NodeTracerProvider } from "@opentelemetry/sdk-trace-node";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";

const accessor = import.meta.env ?? process.env;
const deploymentEnvironment = accessor.VITE_OTEL_DEPLOYMENT_ENVIRONMENT || "local";
const serviceName = accessor.VITE_TELEMETRY_SERVICE_NAME || "opc-web-utilities";
const otlpEndpoint = accessor.VITE_OTEL_EXPORTER_OTLP_ENDPOINT
  ? `${accessor.VITE_OTEL_EXPORTER_OTLP_ENDPOINT}/v1/traces`
  : null;

export function getStandardAttributes(extras = {}) {
  // Baseline attrs (already using "of." where it makes sense)
  const base = {
    env: deploymentEnvironment,
    source: "webjs",
    ddsource: "webjs",
    "deployment.environment": deploymentEnvironment
  };

  // Merge provided extras with auto "of." prefixing
  return ofMerge(base, extras);
}

export const resource = resourceFromAttributes({
  [ATTR_SERVICE_NAME]: serviceName,
  [ATTR_SERVICE_VERSION]: "1.0.0",
  "dd.service": serviceName,
  ddsource: "webjs",
  env: deploymentEnvironment,
  "deployment.environment": deploymentEnvironment
});

export const collectorOptions = {
  url: otlpEndpoint,
  headers: {
    mode: "cors",
    "Referrer-Policy": "origin"
  },
  concurrencyLimit: 10 // an optional limit on pending requests
};

const exporter = new OTLPTraceExporter(collectorOptions);

function initializeOpenTelemetry(provider) {
  const tracingProvider = new provider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(exporter, {
        // The maximum queue size. After the size is reached spans are dropped.
        maxQueueSize: 100,
        // The maximum batch size of every export. It must be smaller or equal to maxQueueSize.
        maxExportBatchSize: 10,
        // The interval between two consecutive exports
        scheduledDelayMillis: 500,
        // How long the export can run before it is cancelled
        exportTimeoutMillis: 30000
      })
    ],
    sampler: {
      //eslint-disable-next-line no-unused-vars
      shouldSample: (context, traceId, spanName, spanKind, attributes, links) => {
        let decision = SamplingDecision.RECORD_AND_SAMPLED;
        if (!includeSpan(spanName)) {
          decision = SamplingDecision.NOT_RECORD;
        }

        return { decision };
      }
    }
  });

  return tracingProvider;
}

//Certain auto-instrumentation spans are VERY noisy so we can configure removing
//them to take the load off the otel collector
const includeSpan = (spanName) => {
  const skipSpans = (import.meta.env.VITE_SKIP_SPAN_RECORDING ?? "").split(",");
  if (skipSpans.includes(spanName)) {
    return false;
  } else return true;
};

export function buildSpanNameFromAttribs(prefix, obj) {
  const label = obj.viewKey || obj.path;
  return `${prefix}${label ? `: ${label}` : ""}`;
}

export async function initializeOpenTelemetryForServer() {
  const tracingProvider = initializeOpenTelemetry(NodeTracerProvider);
  tracingProvider.register();
}

export async function initializeOpenTelemetryForClient() {
  const tracingProvider = initializeOpenTelemetry(WebTracerProvider);
  tracingProvider.register({
    contextManager: new ZoneContextManager()
  });
}
